import axios from 'axios';
import config from '../config';

const { api } = config;

const companiesAxios = axios.create({
  baseURL: `${api.API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
companiesAxios.interceptors.request.use(
  (config) => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      const user = JSON.parse(authUser);
      if (user && user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
companiesAxios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || error.message || 'Erro na requisição';
    return Promise.reject(new Error(message));
  }
);

export interface CompanyDto {
    id: string;
    name: string;
    owner: string;
    industryType: string;
    rating: number;
    location: string;
    logoUrl?: string;
    website?: string;
    contactEmail?: string;
    contactPhone?: string;
    employeeCount?: number;
    foundedYear?: number;
    description?: string;
    isActive: boolean;
    createdAt: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
    // Phase 2+ fields
    cnpj?: string;
    taxRegime?: number;
    companyType?: number;
    cnaePrincipal?: string;
    cnaeSecondary?: string;
    nire?: string;
    simplesCode?: string;
    professionalEntity?: string;
    stateRegistration?: string;
    municipalRegistration?: string;
    employeeRange?: number;
    invoicePassword?: string;
    invoiceEmissionPortalUrl?: string;
    email?: string;
    phone?: string;
    registrationPhase?: number;
    registrationType?: string;
    // CNPJ Lookup fields
    porte?: string;
    capitalSocial?: number;
    dataAbertura?: string;
    // Tax regime conditional fields
    anexo?: string;
    segmento?: string;
    ramoAtividadeId?: string;
    ramoAtividadeNome?: string;
    // Additional fields
    observacao?: string;
    // Headquarters address fields
    hqStreet?: string;
    hqNumber?: string;
    hqComplement?: string;
    hqNeighborhood?: string;
    hqCity?: string;
    hqState?: string;
    hqCep?: string;
    // Company identification
    companyId?: number;
    companyNumber?: number;
    logoStorageId?: string;
    // Partners (Phase 1)
    partners?: Array<{
        id: string;
        name: string;
        cpf: string;
        phone: string;
        rg?: string;
        email?: string;
        senhaGov?: string;
        dataNascimento?: string;
        entidadeClasse?: string;
        address?: {
            cep: string;
            street: string;
            number: string;
            complement?: string;
            neighborhood: string;
            city: string;
            state: string;
        };
    }>;
    // Certificate (Phase 3)
    certificate?: {
        id: string;
        hasEcpf: boolean;
        hasEcnpj: boolean;
    };
}

export interface CreateCompanyRequest {
    name: string;
    owner: string;
    industryType: string;
    rating: number;
    location: string;
    logoUrl?: string;
    website?: string;
    contactEmail?: string;
    contactPhone?: string;
    employeeCount?: number;
    foundedYear?: number;
    description?: string;
}

export interface UpdateCompanyRequest extends CreateCompanyRequest {
    id: string;
}

export const companiesService = {
    /**
     * Get all companies
     */
    getAll: async (): Promise<CompanyDto[]> => {
        return companiesAxios.get('/companies');
    },

    /**
     * Get company by ID
     */
    getById: async (id: string): Promise<CompanyDto> => {
        return companiesAxios.get(`/companies/${id}`);
    },

    /**
     * Create a new company
     */
    create: async (company: CreateCompanyRequest): Promise<CompanyDto> => {
        return companiesAxios.post('/companies', company);
    },

    /**
     * Update an existing company
     */
    update: async (company: UpdateCompanyRequest): Promise<void> => {
        await companiesAxios.put(`/companies/${company.id}`, company);
    },

    /**
     * Delete a company
     */
    delete: async (id: string): Promise<void> => {
        await companiesAxios.delete(`/companies/${id}`);
    },

    /**
     * Delete a partner
     */
    deletePartner: async (companyId: string, partnerId: string): Promise<void> => {
        await companiesAxios.delete(`/companies/${companyId}/partners/${partnerId}`);
    },

    // ===== WIZARD ENDPOINTS =====

    /**
     * Create company - Phase 1 (Partners)
     */
    createPhase1: async (data: any): Promise<any> => {
        try {
            const result = await companiesAxios.post('/companies/phase1', data);
            return result;
        } catch (error: any) {
            console.error('Companies Service - Phase 1 Error:', error);
            console.error('Error response:', error.response?.data);
            throw error;
        }
    },

    /**
     * Update company - Phase 1 (Partners)
     */
    updatePhase1: async (companyId: string, data: any): Promise<any> => {
        const payload = { ...data, companyId };
        try {
            const result = await companiesAxios.put(`/companies/${companyId}/phase1`, payload);
            return result;
        } catch (error: any) {
            console.error('Phase 1 Update Error:', error);
            console.error('Error response data:', error.response?.data);
            throw error;
        }
    },

    /**
     * Update company - Phase 2 (Company Details)
     */
    updatePhase2: async (companyId: string, data: any): Promise<any> => {
        const payload = { ...data, companyId };
        try {
            const result = await companiesAxios.put(`/companies/${companyId}/phase2`, payload);
            return result;
        } catch (error: any) {
            console.error('Phase 2 Update Error:', error);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            console.error('Error message:', error.message);
            throw error;
        }
    },

    /**
     * Update company - Phase 3 (Certificates)
     */
    updatePhase3: async (companyId: string, data: any): Promise<any> => {
        return companiesAxios.put(`/companies/${companyId}/phase3`, { ...data, companyId });
    },

    /**
     * Save company draft
     */
    saveDraft: async (companyId: string): Promise<any> => {
        return companiesAxios.post(`/companies/${companyId}/draft`, {});
    },

    /**
     * Validate CPF
     */
    validateCpf: async (cpf: string): Promise<{ isValid: boolean; formattedCpf?: string; errorMessage?: string }> => {
        return companiesAxios.post('/companies/validate-cpf', { cpf });
    },

    /**
     * Validate CNPJ
     */
    validateCnpj: async (cnpj: string): Promise<{ isValid: boolean; formattedCnpj?: string; errorMessage?: string }> => {
        return companiesAxios.post('/companies/validate-cnpj', { cnpj });
    },

    /**
     * Lookup company data by CNPJ from Receita Federal
     */
    lookupCnpj: async (cnpj: string): Promise<CnpjLookupResponse | null> => {
        try {
            // Remove formatting - send only digits
            const cnpjDigits = cnpj.replace(/\D/g, '');
            return await companiesAxios.get(`/companies/lookup-cnpj/${cnpjDigits}`);
        } catch (error) {
            console.error('CNPJ lookup error:', error);
            return null;
        }
    }
};

export interface CnpjLookupResponse {
    cnpj: string;
    razaoSocial: string;
    nomeFantasia?: string;
    porte?: string;
    situacaoCadastral?: string;
    dataAbertura?: string;
    atividadePrincipal?: {
        codigo: string;
        descricao: string;
    };
    atividadesSecundarias?: Array<{
        codigo: string;
        descricao: string;
    }>;
    endereco?: {
        logradouro?: string;
        numero?: string;
        complemento?: string;
        bairro?: string;
        municipio?: string;
        uf?: string;
        cep?: string;
    };
    email?: string;
    telefone?: string;
    naturezaJuridica?: string;
    capitalSocial?: string;
    opcaoPeloSimples?: boolean;
    opcaoPeloMei?: boolean;
}
